import { useTranslation } from 'react-i18next';
import { Button, ButtonType, ButtonVariant, SearchableSelect } from 'components/core';
import { User } from 'states/UsersState';
import { FilterOption, NO_FILTERS, NO_GEO, UserFilterValue, geoOptions, hasFilters, roleOptions } from 'helpers/userFilters';

interface UserFiltersProps {
  /** The users the filters are built from (so only roles and levels that exist are offered). */
  users: Pick<User, 'roles'>[];
  value: UserFilterValue;
  onChange: (next: UserFilterValue) => void;
}

interface FilterSelectProps {
  label: string;
  /** Shown while nothing is ticked, which means "no restriction". */
  anyLabel: string;
  options: FilterOption[];
  nameOf: (value: string) => string;
  selected: string[];
  onSelect: (values: string[]) => void;
}

const FilterSelect = ({ label, anyLabel, options, nameOf, selected, onSelect }: FilterSelectProps) => (
  <div className="d-flex flex-column" aria-label={label} role="group">
    <span className="small text-secondary mb-1">{label}</span>
    <SearchableSelect
      multiple
      options={options.map((o) => ({ value: o.value, label: nameOf(o.value), detail: String(o.count) }))}
      selectedValues={selected.filter((v) => options.some((o) => o.value === v))}
      onChangeValues={onSelect}
      placeholder={anyLabel}
      searchPlaceholder={label}
    />
  </div>
);

// Narrow the user list to any of several roles and/or levels of geographic scope. Searching still applies on top.
export const UserFilters = ({ users, value, onChange }: UserFiltersProps) => {
  const { t } = useTranslation();
  const geoName = (v: string) => (v === NO_GEO ? t('LIST_VIEW.NO_GEO_SCOPE') : t(`LIST_VIEW.GEO_${v.toUpperCase()}`));
  return (
    // items sit on the bottom edge so the Clear button lines up with the dropdown buttons, not their labels
    <div className="d-flex flex-wrap align-items-end gap-3">
      <FilterSelect
        label={t('LIST_VIEW.FILTER_ROLE')}
        anyLabel={t('LIST_VIEW.ANY_ROLE')}
        options={roleOptions(users)}
        nameOf={(v) => v}
        selected={value.roles}
        onSelect={(roles) => onChange({ ...value, roles })}
      />
      <FilterSelect
        label={t('LIST_VIEW.FILTER_GEO')}
        anyLabel={t('LIST_VIEW.ANY_GEO')}
        options={geoOptions(users)}
        nameOf={geoName}
        selected={value.geos}
        onSelect={(geos) => onChange({ ...value, geos })}
      />
      {hasFilters(value) && (
        <Button htmlType="button" type={ButtonType.secondary} variant={ButtonVariant.outline} onClick={() => onChange(NO_FILTERS)}>
          {t('LIST_VIEW.CLEAR_FILTERS')}
        </Button>
      )}
    </div>
  );
};
